import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Menu, X, Home, ClipboardList, Briefcase, User, ShieldCheck, LifeBuoy,
  Compass, Wallet, BookOpen, GraduationCap, Gift, Award, LogOut,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import NotificationBell from "@/components/NotificationBell";

const TABS = [
  { key: "dashboard", label: "Dashboard", Icon: Home, to: "/profile?tab=dashboard" },
  { key: "applications", label: "Applications", Icon: ClipboardList, to: "/profile?tab=applications" },
  { key: "jobs", label: "Jobs", Icon: Briefcase, to: "/profile?tab=jobs" },
  { key: "profile", label: "Profile", Icon: User, to: "/profile?tab=profile" },
];

const MENU = [
  { key: "kyc", label: "KYC", Icon: ShieldCheck, to: "/profile?tab=kyc" },
  { key: "helpdesk", label: "Helpdesk", Icon: LifeBuoy, to: "/profile?tab=helpdesk" },
  { key: "browse", label: "Browse Programs", Icon: Compass, to: "/profile?tab=browse" },
  { key: "finance", label: "Finance & Invoices", Icon: Wallet, to: "/profile?tab=finance" },
  { key: "courses", label: "My Courses", Icon: BookOpen, to: "/profile?tab=courses" },
  { key: "external", label: "My External Courses", Icon: GraduationCap, to: "/profile?tab=external" },
  { key: "refer", label: "Refer & Earn", Icon: Gift, to: "/refer-earn" },
  { key: "certificates", label: "My Certificates", Icon: Award, to: "/profile?tab=certificates" },
];

export default function StudentMobileNav() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [open, setOpen] = useState(false);
  const tab = new URLSearchParams(search).get("tab") || "dashboard";

  const go = (to) => { setOpen(false); navigate(to); };

  return (
    <>
      {/* ---------- Fixed mobile header (portal only) ---------- */}
      <header
        className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-slate-100 shadow-[0_2px_16px_rgba(15,23,42,0.06)]"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
        data-testid="student-mobile-header"
      >
        <div className="grid grid-cols-[44px_1fr_44px] items-center h-14 px-3">
          <button onClick={() => setOpen(true)} aria-label="Open menu" data-testid="student-hamburger"
            className="w-10 h-10 flex items-center justify-center rounded-xl text-[#17263B] active:bg-slate-100">
            <Menu className="w-6 h-6" />
          </button>
          <Link to="/profile?tab=dashboard" className="flex justify-center" data-testid="student-mobile-logo">
            <BrandLogo className="h-8 w-auto" />
          </Link>
          <div className="flex justify-center" data-testid="student-mobile-bell"><NotificationBell /></div>
        </div>
      </header>

      {/* ---------- Dark left slide-out menu ---------- */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="student-menu-overlay">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} aria-hidden="true" />
          <aside className="absolute left-0 top-0 bottom-0 w-[300px] max-w-[84vw] bg-[#0F1115] flex flex-col shadow-2xl"
            style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
            data-testid="student-sidebar">
            <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
              <BrandLogo className="h-8 w-auto" />
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="student-menu-close"
                className="w-9 h-9 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-3">
              {MENU.map(({ key, label, Icon, to }) => {
                const active = key === "refer" ? false : tab === key;
                return (
                  <button key={key} onClick={() => go(to)} data-testid={`student-menu-${key}`}
                    className={`w-full flex items-center gap-3.5 px-6 py-3.5 text-[15px] font-semibold transition-colors border-l-[3px] ${active ? "text-[#FFC72C] border-[#FFC72C] bg-white/5" : "text-white/85 border-transparent hover:bg-white/5"}`}>
                    <Icon className="w-5 h-5 shrink-0" strokeWidth={active ? 2.3 : 1.9} /> {label}
                  </button>
                );
              })}
            </nav>
            <button onClick={async () => { setOpen(false); await logout(); navigate("/"); }} data-testid="student-menu-logout"
              className="flex items-center gap-3.5 px-6 py-4 text-[15px] font-semibold text-white/85 border-t border-white/10 hover:bg-white/5">
              <LogOut className="w-5 h-5" strokeWidth={1.9} /> Logout
            </button>
          </aside>
        </div>
      )}

      {/* ---------- Fixed bottom tab bar (portal only) ---------- */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-100 shadow-[0_-8px_30px_rgba(15,23,42,0.12)] rounded-t-3xl"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="student-bottom-nav"
        aria-label="Student portal navigation"
      >
        <div className="grid grid-cols-4 items-end px-2 pt-2 pb-2">
          {TABS.map(({ key, label, Icon, to }) => {
            const active = tab === key;
            return (
              <Link key={key} to={to} aria-label={label} data-testid={`student-tab-${key}`}
                className="flex flex-col items-center justify-center gap-1 min-h-[56px] rounded-2xl px-2 py-1.5 transition-colors">
                <Icon className={`w-6 h-6 ${active ? "text-[#17263B]" : "text-slate-400"}`} strokeWidth={active ? 2.4 : 1.9} />
                <span className={`text-[11px] font-bold ${active ? "text-[#17263B]" : "text-slate-400"}`}>{label}</span>
                <span className={`h-1 w-6 rounded-full transition-colors ${active ? "bg-[#FFC72C]" : "bg-transparent"}`} />
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
}
